import {
  clampEraseRadius,
  ERASE_RADIUS_DEFAULT,
  resizeEraseRadius,
  type PrefStore,
} from "./erase-brush.ts";

/** Box → Area → Circle → Box. Fill Roads starts on Box. */
export const ROADS_TOOLS = ["box", "area", "circle"] as const;
export type RoadsTool = (typeof ROADS_TOOLS)[number];

const TOOL_KEY = "mapus.roads-tool";
const RADIUS_KEY = "mapus.roads-brush-radius";

export const ROADS_BRUSH_DEFAULT = ERASE_RADIUS_DEFAULT;

/** Box → Area → Circle → Box. */
export function nextRoadsTool(tool: RoadsTool): RoadsTool {
  if (tool === "box") return "area";
  if (tool === "area") return "circle";
  return "box";
}

export function isRoadsTool(value: unknown): value is RoadsTool {
  return value === "box" || value === "area" || value === "circle";
}

export function readRoadsTool(store: PrefStore): RoadsTool {
  try {
    const raw = store.getItem(TOOL_KEY);
    return isRoadsTool(raw) ? raw : "box";
  } catch {
    return "box";
  }
}

export function writeRoadsTool(store: PrefStore, tool: RoadsTool): void {
  try {
    store.setItem(TOOL_KEY, tool);
  } catch {
    /* private mode */
  }
}

export function clampRoadsBrushRadius(radius: number): number {
  return clampEraseRadius(radius);
}

export function resizeRoadsBrushRadius(radius: number, deltaY: number): number {
  return resizeEraseRadius(radius, deltaY);
}

export function readRoadsBrushRadius(store: PrefStore): number {
  try {
    const raw = store.getItem(RADIUS_KEY);
    if (raw == null || raw === "") return ROADS_BRUSH_DEFAULT;
    return clampRoadsBrushRadius(Number(raw));
  } catch {
    return ROADS_BRUSH_DEFAULT;
  }
}

export function writeRoadsBrushRadius(store: PrefStore, radius: number): void {
  try {
    store.setItem(RADIUS_KEY, String(clampRoadsBrushRadius(radius)));
  } catch {
    /* private mode */
  }
}

/** Pixel corners of an axis-aligned screen rectangle, clockwise from the min corner. */
export function screenRectCorners(
  origin: { x: number; y: number },
  at: { x: number; y: number },
): Array<{ x: number; y: number }> {
  const x0 = Math.min(origin.x, at.x);
  const y0 = Math.min(origin.y, at.y);
  const x1 = Math.max(origin.x, at.x);
  const y1 = Math.max(origin.y, at.y);
  return [
    { x: x0, y: y0 },
    { x: x1, y: y0 },
    { x: x1, y: y1 },
    { x: x0, y: y1 },
  ];
}

/** Both sides of the drag must pass the threshold so a twitch is not a fill. */
export function screenRectReady(
  origin: { x: number; y: number },
  at: { x: number; y: number },
  minPx: number,
): boolean {
  return Math.abs(at.x - origin.x) >= minPx && Math.abs(at.y - origin.y) >= minPx;
}
